import { useState } from "react";

import { useLoadingStore } from "@/stores/Loading";

import { TabMenu } from "@/types/Shared/tab";
import {
  PastBusinessFilter,
  PastBusinessCorperation,
  PastBusinessDminst,
  PastBusinessBidItem,
} from "@/types/pastbusiness";

import ContentLayout from "@/layouts/ContentLayout";

import {
  ContractMethodList,
  DefaultDateRange,
  DefaultPastBusinessFilter,
} from "@/consts/PastBusiness";

import {
  GetPastBusinessBidList,
  GetPastBusinessBidListWithResult,
  GetPastBusinessCorperationList,
  GetPastBusinessDminstList,
  GetPastBusinessTotalAmount,
} from "@/services/PastBusiness";

import { cn, getDate, isNotEmpty } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import CommonTab from "@/components/Common/Tab";
import PastBusinessSearch from "@/components/PastBusinessSearch";
import CommonInput from "@/components/Common/Input";
import CommonImg from "@/components/Common/Img";
import CommonSelect from "@/components/Common/Select";
import CommonPagination from "@/components/Common/Pagination";

import SearchIcon from "@/assets/images/search-icon.svg";
import SortIcon from "@/assets/images/sort-icon.svg";
import SortDescIcon from "@/assets/images/sort-desc-icon.svg";

export default function PastBusiness() {
  const { startLoading, endLoading } = useLoadingStore();

  const tabData: TabMenu[] = [{ label: "과거 사업", path: "/past" }];
  const [didSearched, setDidSearched] = useState<boolean>(false);
  const [filter, setFilter] = useState<PastBusinessFilter>(
    DefaultPastBusinessFilter
  );
  const [dateRange, setDateRange] = useState<string>(DefaultDateRange);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [, setCorperationSort] = useState<string>("");
  const [corperationList, setCorperationList] = useState<
    PastBusinessCorperation[] | []
  >([]);
  const [, setDminstSort] = useState<string>("");
  const [dminstList, setDminstList] = useState<PastBusinessDminst[] | []>([]);
  const [bidList, setBidList] = useState<PastBusinessBidItem[] | []>([]);

  const [searchCorperation, setSearchCorperation] = useState<string>("");
  const [searchDminst, setSearchDminst] = useState<string>("");

  const getBasicFilterParams = (propFilter?: PastBusinessFilter) =>
    `search_request = BidContractSearchRequest(` +
    `keywords=${JSON.stringify(propFilter?.keywords ?? filter.keywords)}, ` +
    `bool_op="${propFilter?.bool_op ?? filter.bool_op}", ` +
    `category="${propFilter?.category ?? filter.category}", ` +
    `begin_dt="${propFilter?.begin_dt ?? filter.begin_dt}", ` +
    `end_dt="${propFilter?.end_dt ?? filter.end_dt}", ` +
    `min_amount=${propFilter?.min_amount ?? filter.min_amount}, ` +
    `max_amount=${propFilter?.max_amount ?? filter.max_amount}, ` +
    `page_no=1, ` +
    `page_size=${propFilter?.page_size ?? filter.page_size}` +
    `)\n`;
  const getParams = (propFilter?: PastBusinessFilter) => {
    const newParams =
      `search_request = BidContractSearchRequest(` +
      `keywords=${JSON.stringify(propFilter?.keywords ?? filter.keywords)}, ` +
      `bool_op="${propFilter?.bool_op ?? filter.bool_op}", ` +
      `category="${propFilter?.category ?? filter.category}", ` +
      `begin_dt="${propFilter?.begin_dt ?? filter.begin_dt}", ` +
      `end_dt="${propFilter?.end_dt ?? filter.end_dt}", ` +
      `dmin_keyword="${propFilter?.dmin_keyword ?? filter.dmin_keyword}", ` +
      `corp_keyword="${propFilter?.corp_keyword ?? filter.corp_keyword}", ` +
      `min_amount=${propFilter?.min_amount ?? filter.min_amount}, ` +
      `max_amount=${propFilter?.max_amount ?? filter.max_amount}, ` +
      `sort_by="${propFilter?.sort_by ?? filter.sort_by}", ` +
      `order="${propFilter?.order ?? filter.order}", ` +
      `page_no=${propFilter?.page_no ?? filter.page_no}, ` +
      `page_size=${propFilter?.page_size ?? filter.page_size}` +
      `${filter.contract_method ? `, contract_method="${filter.contract_method}"` : ``}` +
      `)\n`;
    return newParams;
  };

  const getTotalAmount = async (propFilter?: PastBusinessFilter) => {
    try {
      const params: string = propFilter
        ? getParams(propFilter)
        : getBasicFilterParams();
      const totalAmountResult = await GetPastBusinessTotalAmount({
        input: params.concat(`bid.contract.get_contract_stats(search_request)`),
      });
      setTotalAmount(totalAmountResult.data.pods[0].content.total_amount);
    } catch (e) {
    } finally {
    }
  };
  const getCorperationList = async (propFilter?: PastBusinessFilter) => {
    try {
      startLoading("past-corp-search");
      const params: string = propFilter
        ? getParams(propFilter)
        : getBasicFilterParams();
      const corperationListResult = await GetPastBusinessCorperationList({
        input: params.concat(`bid.contract.search_by_corp(search_request)`),
      });
      setCorperationList(corperationListResult.data.pods[0].content.items);
    } catch (e) {
    } finally {
      endLoading("past-corp-search");
    }
  };

  const getDminstList = async (propFilter?: PastBusinessFilter) => {
    try {
      startLoading("past-dminst-search");
      const params: string = propFilter
        ? getParams(propFilter)
        : getBasicFilterParams();
      const dminstListResult = await GetPastBusinessDminstList({
        input: params.concat(`bid.contract.search_by_dmin(search_request)`),
      });
      setDminstList(dminstListResult.data.pods[0].content.items);
    } catch (e) {
    } finally {
      endLoading("past-dminst-search");
    }
  };

  const getPastBusinessBidList = async (propFilter?: PastBusinessFilter) => {
    try {
      startLoading("past-bid-search");
      const params: string = propFilter
        ? getParams(propFilter)
        : getBasicFilterParams();
      const bidListResult = await GetPastBusinessBidList({
        input: params.concat(`bid.contract.search_by_keywords(search_request)`),
      });
      setBidList(bidListResult.data.pods[0].content.items);
      setTotalCount(bidListResult.data.pods[0].content.total_hits);
    } catch (e) {
    } finally {
      endLoading("past-bid-search");
    }
  };

  const getPastBusinessBidListWithResult = async () => {
    try {
      startLoading("past-result-search");
      const params: string = getParams();
      const bidListResult = await GetPastBusinessBidListWithResult({
        input: params.concat(
          `bid.contract.search_within_results(search_request)`
        ),
      });
      setBidList(bidListResult.data.pods[0].content.items);
      setTotalCount(bidListResult.data.pods[0].content.total_hits);
    } catch (e) {
    } finally {
      endLoading("past-result-search");
    }
  };

  const getBidList = (propFilter?: PastBusinessFilter) => {
    getTotalAmount(propFilter);
    getPastBusinessBidList(propFilter);
  };

  const onSearch = () => {
    showToast({ message: "공고를 검색합니다." });
    setDidSearched(true);
    getCorperationList();
    getDminstList();
    getBidList();
    setFilter((prev) => ({
      ...prev,
      corp_keyword: "",
      dmin_keyword: "",
      contract_method: undefined,
    }));
  };
  return (
    <ContentLayout>
      <CommonTab variants="string" data={tabData} />
      <PastBusinessSearch
        filter={filter}
        setFilter={setFilter}
        dateRange={dateRange}
        setDateRange={setDateRange}
        onSearch={onSearch}
      />
      {didSearched && (
        <div className="flex flex-col gap-[20px]">
          <div className="flex flex-col gap-[8px]">
            <div className="text-l font-bold">주요 통계</div>
            <div className="min-w-[1100px] overflow-auto bg-white border-[1px] border-gray-D rounded-t-sm flex">
              <div className="w-full flex flex-col">
                <div className="p-2 flex gap-[8px] items-center">
                  <CommonInput
                    placeholder="기업 검색"
                    defaultValue={searchCorperation}
                    onChange={setSearchCorperation}
                    onKeyDownEnter={(value) => {
                      getCorperationList({
                        ...filter,
                        corp_keyword: value,
                      });
                    }}
                  />
                  <CommonImg
                    className="p-1 bg-dark-brown rounded-sm size-[26px] cursor-pointer"
                    src={SearchIcon}
                    alt="Search"
                    onClick={() => {
                      getCorperationList({
                        ...filter,
                        corp_keyword: searchCorperation,
                      });
                    }}
                  />
                </div>
                <div className="flex">
                  <table className="w-full">
                    <thead>
                      <tr className="table w-full bg-dark-brown text-white text-[12px]">
                        <th className="w-[10%]">순위</th>
                        <th className="w-[50%]">기업</th>
                        <th className="w-[15%]">
                          <div
                            className="flex gap-[4px] justify-center items-center cursor-pointer"
                            onClick={() => {
                              setCorperationSort(() => {
                                const sort_by = "num_contracts";
                                const order = "DESC";
                                const newSort = `${sort_by}|${order}`;
                                getCorperationList({
                                  ...filter,
                                  sort_by: "num_contracts",
                                  order: "DESC",
                                });
                                return newSort;
                              });
                            }}
                          >
                            계약 건 수
                            <CommonImg src={SortDescIcon} alt="Sort Desc" />
                          </div>
                        </th>
                        <th className="w-[25%]">
                          <div
                            className="flex gap-[4px] justify-center items-center cursor-pointer"
                            onClick={() => {
                              setCorperationSort((prev) => {
                                const prevSort = prev.split("|");
                                const sort_by = "budget_amount";
                                const order =
                                  prevSort[1] === "DESC" ? "ASC" : "DESC";
                                const newSort = `${sort_by}|${order}`;
                                getCorperationList({
                                  ...filter,
                                  sort_by: sort_by,
                                  order: order,
                                });
                                return newSort;
                              });
                            }}
                          >
                            총 금액
                            <CommonImg src={SortIcon} alt="Sort" />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="block h-[150px] overflow-auto">
                      {isNotEmpty(corperationList) &&
                        corperationList.map((corp, corpIndex) => (
                          <tr
                            key={corpIndex}
                            className={cn(
                              "table w-full text-[12px] hover:bg-gray-D cursor-pointer",
                              {
                                "bg-dark-red-60":
                                  filter.corp_keyword === corp.corp_name,
                              }
                            )}
                            onClick={() => {
                              setFilter((prev) => {
                                const newFilter = {
                                  ...prev,
                                  corp_keyword:
                                    prev.corp_keyword === corp.corp_name
                                      ? ""
                                      : corp.corp_name,
                                };
                                getBidList(newFilter);
                                return newFilter;
                              });
                            }}
                          >
                            <td className="w-[10%] py-[6px] text-center">
                              {corp.rank ?? corpIndex + 1}
                            </td>
                            <td className="w-[50%] py-[6px] text-left">
                              {corp.corp_name}
                            </td>
                            <td className="w-[15%] py-[6px] px-[10px] text-right">
                              {corp.count}
                            </td>
                            <td className="w-[25%] py-[6px] px-[10px] text-right">
                              {corp.total_amount.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="w-full flex flex-col">
                <div className="p-2 flex gap-[8px] items-center">
                  <CommonInput
                    placeholder="기관 검색"
                    defaultValue={searchDminst}
                    onChange={setSearchDminst}
                    onKeyDownEnter={(value) => {
                      getDminstList({
                        ...filter,
                        dmin_keyword: value,
                      });
                    }}
                  />
                  <CommonImg
                    className="p-1 bg-dark-brown rounded-sm size-[26px] cursor-pointer"
                    src={SearchIcon}
                    alt="Search"
                    onClick={() => {
                      getDminstList({
                        ...filter,
                        dmin_keyword: searchCorperation,
                      });
                    }}
                  />
                </div>
                <div className="flex">
                  <table className="w-full border-l-[2px] border-gray-D">
                    <thead>
                      <tr className="table w-full bg-dark-brown text-white text-[12px]">
                        <th className="w-[10%]">순위</th>
                        <th className="w-[50%]">수요기관</th>
                        <th className="w-[15%]">
                          <div
                            className="flex gap-[4px] justify-center items-center cursor-pointer"
                            onClick={() => {
                              setDminstSort(() => {
                                const sort_by = "num_contracts";
                                const order = "DESC";
                                const newSort = `${sort_by}|${order}`;
                                getDminstList({
                                  ...filter,
                                  sort_by: sort_by,
                                  order: order,
                                });
                                return newSort;
                              });
                            }}
                          >
                            계약 건 수
                            <CommonImg src={SortDescIcon} alt="Sort Desc" />
                          </div>
                        </th>
                        <th className="w-[25%]">
                          <div
                            className="flex gap-[4px] justify-center items-center cursor-pointer"
                            onClick={() => {
                              setDminstSort((prev) => {
                                const prevSort = prev.split("|");
                                const sort_by = "budget_amount";
                                const order =
                                  prevSort[1] === "DESC" ? "ASC" : "DESC";
                                const newSort = `${sort_by}|${order}`;
                                getDminstList({
                                  ...filter,
                                  sort_by: sort_by,
                                  order: order,
                                });
                                return newSort;
                              });
                            }}
                          >
                            총 금액
                            <CommonImg src={SortIcon} alt="Sort" />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="block h-[150px] overflow-auto">
                      {isNotEmpty(dminstList) &&
                        dminstList.map((dminst, dminstIndex) => (
                          <tr
                            key={dminstIndex}
                            className={cn(
                              "table w-full text-[12px] hover:bg-gray-D cursor-pointer",
                              {
                                "bg-dark-red-60":
                                  filter.dmin_keyword === dminst.dmin_name,
                              }
                            )}
                            onClick={() => {
                              setFilter((prev) => {
                                const newFilter = {
                                  ...prev,
                                  dmin_keyword:
                                    prev.dmin_keyword === dminst.dmin_name
                                      ? ""
                                      : dminst.dmin_name,
                                };
                                getBidList(newFilter);
                                return newFilter;
                              });
                            }}
                          >
                            <td className="w-[10%] py-[6px] text-center">
                              {dminst.rank ?? dminstIndex + 1}
                            </td>
                            <td className="w-[50%] py-[6px] text-left">
                              {dminst.dmin_name}
                            </td>
                            <td className="w-[15%] py-[6px] px-[10px] text-right">
                              {dminst.count}
                            </td>
                            <td className="w-[25%] py-[6px] px-[10px] text-right">
                              {dminst.total_amount.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {didSearched && (
        // {isNotEmpty(bidList) && (
        <div className="flex flex-col gap-[20px]">
          <div className="flex flex-col gap-[8px]">
            <div className="flex justify-between">
              <div className="flex gap-[14px] items-center">
                <div className="text-l font-bold">사업 목록</div>
                <div className="text-[12px] text-gray-B font-semibold">
                  * 검색 결과는 페이지 당 최대 100개씩 조회됩니다.
                </div>
              </div>
            </div>
            <div className="min-w-[1100px] overflow-auto bg-white border-[1px] border-gray-D rounded-t-sm flex">
              <div className="w-full flex flex-col">
                <div className="p-2 flex gap-[8px] items-center">
                  <CommonSelect
                    defaultValue={filter.contract_method ?? "ALL"}
                    optionList={ContractMethodList}
                    onChange={(value) => {
                      setFilter((prev) => ({
                        ...prev,
                        contract_method: value,
                      }));
                    }}
                  />
                  <CommonImg
                    className="p-1 bg-dark-brown rounded-sm size-[26px] cursor-pointer"
                    src={SearchIcon}
                    alt="Search"
                    onClick={getPastBusinessBidListWithResult}
                  />
                  <div className="w-[100px] text-[12px] text-center">
                    {totalCount.toLocaleString()} 공고
                  </div>
                  <div className="w-[200px] text-[12px] text-left">
                    총 {totalAmount.toLocaleString()}원
                  </div>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-dark-brown text-white text-[12px]">
                      <th className="py-[3px]">공고명</th>
                      <th className="w-[50px]">계약 차수</th>
                      <th>
                        <div
                          className="flex gap-[4px] justify-center items-center cursor-pointer"
                          onClick={() => {
                            setFilter((prev) => {
                              const newFilter = {
                                ...prev,
                                sort_by: "budget_amount",
                                order: prev.order === "DESC" ? "ASC" : "DESC",
                              };
                              getBidList(newFilter);

                              return newFilter;
                            });
                          }}
                        >
                          규모
                          <CommonImg src={SortIcon} alt="Sort" />
                        </div>
                      </th>
                      <th>구분</th>
                      <th className="w-[50px]">계약방식</th>
                      <th>분류</th>
                      <th>수요 기관</th>
                      <th>수주 기업</th>
                      <th>
                        <div
                          className="flex gap-[4px] justify-center items-center cursor-pointer"
                          onClick={() => {
                            setFilter((prev) => {
                              const newFilter = {
                                ...prev,
                                sort_by: "upload_date",
                                order: "DESC",
                              };
                              getBidList(newFilter);

                              return newFilter;
                            });
                          }}
                        >
                          날짜
                          <CommonImg src={SortDescIcon} alt="Sort Desc" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isNotEmpty(bidList) &&
                      bidList.map((bid, bidIndex) => (
                        <tr
                          key={bidIndex}
                          className="text-[12px] hover:bg-gray-D cursor-pointer border-t-2 border-gray-D"
                        >
                          <td className="px-[20px] py-[5px]">
                            <div className="flex flex-col">
                              <div>{bid.contract_name}</div>
                              <div className="pt-[4px] flex gap-[4px]">
                                {bid.matched_keywords.map(
                                  (keyword, keywordIndex) => (
                                    <div
                                      key={keywordIndex}
                                      className="bg-dark-red rounded-xl px-[10px] py-[2px] text-white"
                                    >
                                      {keyword}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            {bid.decision_contract_ord}차
                          </td>
                          <td className="text-center">
                            {bid.contract_amount.toLocaleString()}원
                          </td>
                          <td className="text-center">
                            {bid.business_division_name}
                          </td>
                          <td className="text-center">
                            {bid.contract_method_name}
                          </td>
                          <td className="text-center">
                            {bid.large_classification_name}
                          </td>
                          <td className="text-center">{bid.dmin_name}</td>
                          <td className="text-center">{bid.corp_name}</td>
                          <td className="text-center">
                            {getDate(bid.registration_dt)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <CommonPagination
                totalItems={totalCount}
                itemsPerPage={filter.page_size}
                currentPage={filter.page_no}
                onPageChange={(page) => {
                  setFilter((prev) => {
                    const newFilter = { ...prev, page_no: page };
                    getBidList(newFilter);
                    return newFilter;
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}
    </ContentLayout>
  );
}
